import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";

type AppNavigationGuard = () => boolean;

type AppNavigationGuardContextValue = {
  registerGuard: (guard: AppNavigationGuard) => () => void;
  canNavigate: () => boolean;
};

const UNGUARDED_CONTEXT: AppNavigationGuardContextValue = {
  registerGuard: () => () => undefined,
  canNavigate: () => true,
};

const AppNavigationGuardContext = createContext<AppNavigationGuardContextValue>(UNGUARDED_CONTEXT);

export function AppNavigationGuardProvider({ children }: { children: React.ReactNode }) {
  const guardRef = useRef<AppNavigationGuard | null>(null);

  const registerGuard = useCallback((guard: AppNavigationGuard) => {
    guardRef.current = guard;
    return () => {
      if (guardRef.current === guard) {
        guardRef.current = null;
      }
    };
  }, []);

  const canNavigate = useCallback(() => guardRef.current?.() ?? true, []);
  const value = useMemo(() => ({ registerGuard, canNavigate }), [canNavigate, registerGuard]);

  return (
    <AppNavigationGuardContext.Provider value={value}>
      {children}
    </AppNavigationGuardContext.Provider>
  );
}

export function useAppNavigationGuard() {
  return useContext(AppNavigationGuardContext);
}

export function useAppNavigationGuardRegistration(guard: AppNavigationGuard | null) {
  const { registerGuard } = useAppNavigationGuard();

  useEffect(() => {
    if (!guard) return undefined;
    return registerGuard(guard);
  }, [guard, registerGuard]);
}
